import React, { FC, useMemo } from 'react';
import { ExarePriceDetailsPriceElementType, RefundPricesFragment } from '@websky/graphql';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../../../../../theme';
import PriceItem from '../PriceItem/PriceItem';
import { Money as MoneyType } from '../../../../../../../types';
import TotalRefund from '../TotalRefund/TotalRefund';
import { getTotalPriceFilteredElements, getZeroPrice } from '@shared/lib/money';

interface Props {
	prices: RefundPricesFragment;
	ticketPrice?: MoneyType;
	className?: string;
}

const ShortDetails: FC<Props> = ({ prices, ticketPrice, className }) => {
	const { t } = useTranslation('Refund');
	const { ShortDetails: css } = useTheme('Entities').Order.ExchangeRefundPriceDetails;

	const details = useMemo(() => {
		const items = [];

		const priceDetails = prices.priceDetails;
		const currentCurrency = prices.basePrice?.currency;

		items.push({
			name: `${t('Ticket price')}:`,
			price: prices.basePrice || getZeroPrice(currentCurrency)
		});

		items.push({
			name: t('Cost of refundable services'),
			price: getTotalPriceFilteredElements(
				priceDetails.refunded.elements,
				ExarePriceDetailsPriceElementType.gdsServiceFare,
				currentCurrency
			)
		});

		items.push({
			name: t('Cost of non-refundable services'),
			price: getTotalPriceFilteredElements(
				priceDetails.retention.elements,
				ExarePriceDetailsPriceElementType.gdsServiceFare,
				currentCurrency
			),
			isMinus: true
		});

		items.push({
			name: t('Fare refund penalty'),
			price: getTotalPriceFilteredElements(
				priceDetails.retention.elements,
				ExarePriceDetailsPriceElementType.taxesRetention,
				currentCurrency
			),
			isMinus: true
		});

		return items;
	}, [t, prices, ticketPrice]);

	return (
		<div className={cn(css.root, className)}>
			<div className={css.details}>
				{details.length > 0 &&
					details.map((detail, index) => (
						<PriceItem
							key={index}
							className={css.item}
							name={detail.name}
							price={detail.price}
							isMinus={detail.isMinus}
						/>
					))}
			</div>

			<TotalRefund className={css.totalRefund} price={prices.priceToPay} />
		</div>
	);
};

export default ShortDetails;
